import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { getCustomFormComponentControlValueAccessorProvider } from '@lx/quote-tool/lite/shared/utils';
import { SingleSelectionButtonListFieldItem } from './single-selection-button-list-field.type';

@Component({
  standalone: true,
  selector: 'lx-single-selection-button-list-field',
  templateUrl: './single-selection-button-list-field.component.html',
  styleUrls: ['./single-selection-button-list-field.component.scss'],
  imports: [CommonModule, ButtonModule, FormsModule],
  providers: [getCustomFormComponentControlValueAccessorProvider(SingleSelectionButtonListFieldComponent)],
})
export class SingleSelectionButtonListFieldComponent<T> implements ControlValueAccessor, OnChanges {
  @Input() title: string;
  @Input() options: SingleSelectionButtonListFieldItem<T>[] = [];
  @Input() disabled = false;
  @Input() extended = false;
  @Input() preselected = true;
  @Input() compareWith: (a: T, b: T) => boolean = (a, b) => a === b;
  @Input() bindValue = 'value';
  @Input() bindLabel = 'name';

  value: T | null = null;
  selectionStates: boolean[] = [];

  ngOnChanges({ options }: SimpleChanges) {
    if (options?.currentValue) {
      const found = options.currentValue.some((o: SingleSelectionButtonListFieldItem<T>) => this.compareWith(o[this.bindValue], this.value));
      if (!found && this.preselected) {
        this.value = options.currentValue.length > 0 ? options.currentValue[0][this.bindValue] ?? null : null;
      }

      this.updateSelectionStates(this.value);
    }
  }

  private onChange: (value: T) => void = () => {
    // onChange triggered but not registered.
  };

  private onTouched: () => void = () => {
    // onTouched triggered but not registered.'
  };

  writeValue(value: T): void {
    this.value = value;
    this.updateSelectionStates(value);
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectValue(optionValue: T): void {
    this.value = optionValue;
    this.onChange(optionValue);
    this.onTouched();
    this.updateSelectionStates(optionValue);
  }

  updateSelectionStates(value: T): void {
    this.selectionStates = this.options?.map((option) => this.compareWith(value, option[this.bindValue]));
  }
}
